<template>
    <address>
        <div v-if="contact" v-fs-block>
            <p v-if="contact.displayName" class="text-muted">{{contact.displayName}}</p>
            <p v-if="contact.organization" class="text-muted">{{contact.organization}}</p>
            <p v-if="contact.address" class="text-muted">{{contact.address}}</p>
            <p v-if="contact.address2" class="text-muted">{{contact.address2}}</p>
            <p v-if="contact.city || contact.region || contact.postalCode" class="text-muted">
                {{contact.city}} {{contact.region}} {{contact.postalCode}}
            </p>
            <p v-if="contact.country" class="text-muted">{{contact.country}}</p>
            <div v-if="contact.phoneNumbers && contact.phoneNumbers.length">
                <ul class="list list-group">
                    <li v-for="phoneNumber in contact.phoneNumbers">
                        <span v-if="phoneNumber.primary" class="text-muted">{{phoneNumber.value}} ({{phoneNumber.label}})</span>
                    </li>
                </ul>
            </div>
            <div v-if="contact.emails && contact.emails.length" @mouseenter="showEmailActions = true" @mouseleave="showEmailActions = false">
                <ul class="list list-group">
                    <li v-for="email in contact.emails">
                        <template v-if="emailActions">
                            <r-button type="link" :disabled="isSendingEmail" v-if="email.primary || email.isLabelPrimary" @click="openEmailModal(email)" class="cursor-pointer inline-xs">{{email.value}}</r-button>
                            <v-icon v-if="isSendingEmail" icon="loading" class="inline-xs is-spinning"/>
                        </template>
                        <template v-else>
                            <span v-if="email.primary  || email.isLabelPrimary" class="text-muted">{{email.value}}</span>
                        </template>
                        <template v-if="emailActions && showEmailActions">
                            <v-icon-button @click="copyEmail(email.value)" class="inline-xs" type="basic" tooltip="Copy Email" size="small" fitted><v-icon icon="clone"/></v-icon-button>
                            <a :href="emailDomainURL(email.value)" class="cursor-pointer has-tooltip-top" target="_blank" data-tooltip="Visit Domain"><v-icon icon="external-link"/></a>
                        </template>
                    </li>
                </ul>
            </div>
        </div>
        <div v-else>
            <p class="text-muted">No contact information available.</p>
        </div>
        <v-modal title="Send Email" @close="showSendEmailModal = false" size="large" scroll v-if="showSendEmailModal">
            <div slot="content">
                <div class="grid-content">
                    <div class="grid-item grid-item-full" v-if="$v.$error">
                        <v-error-messages>
                            <li class="list-item" v-if="!$v.emailMessage.subject.required">Please provide a subject for the email.</li>
                            <li class="list-item" v-if="!$v.emailMessage.text.required">Please provide a body for the email.</li>
                        </v-error-messages>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="To"
                            disabled
                            v-model="emailMessageTo"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Subject"
                            :validate="$v.emailMessage.subject"
                            v-model="emailMessage.subject"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Email Body"
                            multiline
                            placeholder="Your message here"
                            rows="8"
                            :validate="$v.emailMessage.text"
                            v-model="emailMessage.text"/>
                    </div>
                </div>
            </div>
            <template slot="actions">
                <r-button
                    type="primary" @click="sendEmail">Send</r-button>
            </template>
        </v-modal>
    </address>
</template>

<script>
    import {
        vIcon,
        vErrorMessages,
        vIconButton,
    } from '@/components/form';
    import vModal from '@/components/modal/v-modal';
    import {required} from 'vuelidate/lib/validators';
    import ContactModel from '@/common/models/contact-model';
    import EmailMessageModel from '@/common/models/email-message-model';
    import Clipboard from '@/common/clipboard';

    export default {
        name: 'v-contact',
        components: {
            vIcon,
            vIconButton,
            vErrorMessages,
            vModal,
        },
        props: {
            contact: {
                type: ContactModel,
            },
            emailActions: {
                type: Boolean,
            },
        },
        data() {
            return {
                showEmailActions: false,
                showSendEmailModal: false,
                isSendingEmail: false,
                emailMessageTo: '',
                emailMessage: new EmailMessageModel({
                    to: null,
                    from: this.$store.getters['user/email'],
                    subject: `Dear ${this.contact.fullName}`,
                    text: null,
                    metadata: {
                        eventType: 'direct-email-sent',
                    },
                }),
            };
        },
        validations: {
            emailMessage: {
                subject: {required},
                text: {required},
            },
        },
        methods: {
            async copyEmail(data) {
                try {
                    await Clipboard.copy(data);
                    this.$toast.positive('Email copied successfully');
                } catch (error) {
                    this.$toast.negative(`Something went wrong`);
                }
            },
            openEmailModal({value}) {
                this.$v.$reset();
                this.emailMessageTo = value;
                this.emailMessage.to = [this.emailMessageTo];
                this.showSendEmailModal = !this.showSendEmailModal;
                this.showEmailActions = false;
            },
            emailDomainURL(email) {
                const atPosition = email.indexOf('@') + 1;
                return `//www.${email.slice(atPosition)}`;
            },
            async sendEmail() {
                this.isSendingEmail = true;
                const data = this.emailMessage.getPayload();
                this.$v.$touch();
                if (!this.$v.$error) {
                    this.showSendEmailModal = false;
                    try {
                        const message = await this.$api(({emailMessages}) => emailMessages.create({data}));
                        await this.$api(({emailMessages}) => emailMessages.send({id: message.fields.id}));
                        this.$toast.positive('Email has been sent.');
                        this.$emit('email-sent');
                    } catch (error) {
                        this.$toast.fromError(error, 'Unable to send the email. Please try again later or contact support.');
                    }
                }
                this.emailMessage.text = null;
                this.isSendingEmail = false;
            },
        },
    };
</script>
